/*
  PDP media gallery driver — snippets/product-gallery.liquid
  ----------------------------------------------------------
  Mounts every `[data-pdp-gallery]` and owns four things Splide does not give us:

    • Controls. Splide's arrows and pagination are off — the design puts the arrows and
      bar under the images and the dots in a pill over them. Disabled states come from
      `Controller.getEnd()`, the one source of truth for "no further page".

    • The progress bar, matching driveProgressBar() in assets/carousel.js.

    • The zoom lightbox: a <dialog> on a high-resolution source, with a 2x toggle that
      pans by following the pointer.

    • `data-header-tone` on the root, republished as slides change so the overlaying
      header can pick its logo artwork in CSS.

  Unlike assets/carousel.js this mounts unconditionally instead of gating on overflow:
  one instance serves both breakpoints, and a desktop pair always overflows on mobile.
*/
(function () {
  if (window.__productGallery) return;
  window.__productGallery = true;

  var GAP = 4;

  /* ---------- Mount ---------- */

  function setup(root) {
    if (root.__pdpGallery) return;

    var carousel = root.querySelector('[data-pdp-gallery-carousel]');
    var list = carousel && carousel.querySelector('.pdp-gallery__list');
    if (!list) return;

    var state = {
      root: root,
      list: list,
      all: [].slice.call(list.querySelectorAll('.pdp-gallery__slide')),
      splide: null
    };
    if (!state.all.length) return;

    var splide = new Splide(carousel, {
      type: 'slide',
      // Widths live in CSS, so `lifestyle_gallery` needs no second config.
      autoWidth: true,
      perMove: 1,
      // Last page sits flush with the end of the track rather than scrolling past it.
      omitEnd: true,
      arrows: false,
      pagination: false,
      drag: true,
      speed: 400,
      gap: GAP + 'px',
      keyboard: 'focused',
      slideFocus: false,
      label: root.getAttribute('data-label') || undefined
    });

    var prev = root.querySelector('[data-pdp-gallery-prev]');
    var next = root.querySelector('[data-pdp-gallery-next]');
    var bar = root.querySelector('[data-pdp-gallery-progress]');
    var dotsSlot = root.querySelector('[data-pdp-gallery-dots]');
    var dots = [];

    function lastIndex() {
      return splide.Components.Controller.getEnd();
    }

    function syncControls(index) {
      var at = typeof index === 'number' ? index : splide.index;
      var last = lastIndex();
      // Two images on desktop have no second page, so the controls would lie.
      root.classList.toggle('is-scrollable', last > 0);
      if (prev) prev.disabled = at <= 0;
      if (next) next.disabled = at >= last;
    }

    // Same bar as every other carousel — see driveProgressBar() in assets/carousel.js.
    // Off the track position, not the slide index, so it stays truthful mid-drag.
    function rateFor(pos) {
      var move = splide.Components.Move;
      var from = move.getLimit(false);
      var to = move.getLimit(true);
      if (to === from) return 1;
      return Math.min(Math.max((pos - from) / (to - from), 0), 1);
    }

    function setBar(rate) {
      if (bar) bar.style.width = 100 * rate + '%';
    }

    function syncProgress() {
      setBar(rateFor(splide.Components.Move.getPosition()));
    }

    function syncDots(index) {
      var at = typeof index === 'number' ? index : splide.index;
      for (var i = 0; i < dots.length; i++) {
        var on = i === at;
        dots[i].classList.toggle('is-active', on);
        dots[i].setAttribute('aria-current', on ? 'true' : 'false');
      }
    }

    function buildDots() {
      if (!dotsSlot) return;
      dotsSlot.textContent = '';
      dots = [];

      var total = lastIndex() + 1;
      // One dot conveys nothing, and an empty slot is what CSS hides with `:empty`.
      if (total < 2) return;

      // Translated by Liquid — a plain asset cannot call the `t` filter itself.
      var label = dotsSlot.getAttribute('data-dot-label') || 'Go to image %INDEX%';
      var listEl = document.createElement('ul');
      listEl.className = 'pdp-gallery__dots-list';

      for (var i = 0; i < total; i++) {
        (function (index) {
          var item = document.createElement('li');
          var dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'pdp-gallery__dot';
          dot.setAttribute('aria-label', label.replace('%INDEX%', index + 1));
          dot.addEventListener('click', function () {
            splide.go(index);
          });
          item.appendChild(dot);
          listEl.appendChild(item);
          dots.push(dot);
        })(i);
      }

      dotsSlot.appendChild(listEl);
      syncDots();
    }

    /*
      Overlay-header tone. Liquid renders `data-header-tone` for the first paint; this
      keeps it true as the carousel moves, reading `data-slide-tone` off the slide.

      The logo sits over the leftmost visible slide, which with `perMove: 1` is the
      index Splide reports — the slide paired beside it never reaches the logo. Settled
      positions only: a cross-fade chasing a finger mid-drag would flicker.
    */
    function syncTone(index) {
      var at = typeof index === 'number' ? index : splide.index;
      var entry = splide.Components.Slides.getAt(at);
      var slide = entry && entry.slide;
      var light = slide && slide.getAttribute('data-slide-tone') === 'light';
      root.setAttribute('data-header-tone', light ? 'light' : 'dark');
    }

    if (prev) {
      prev.addEventListener('click', function () {
        splide.go('<');
      });
    }
    if (next) {
      next.addEventListener('click', function () {
        splide.go('>');
      });
    }

    splide.on('mounted updated refresh resized moved dragged scrolled', function () {
      syncControls();
      syncDots();
      syncProgress();
      syncTone();
    });
    // The destination is known before the animation ends, so the controls and bar run
    // off it rather than lagging a slide behind.
    splide.on('move', function (index, prev, dest) {
      syncControls(index);
      syncDots(index);
      syncTone(index);
      setBar(rateFor(splide.Components.Move.toPosition(dest)));
    });
    splide.on('mounted updated refresh resized', buildDots);
    // Follow the finger 1:1 while dragging, then hand the easing back.
    splide.on('drag', function () {
      if (bar) bar.style.transition = 'none';
    });
    splide.on('dragging', syncProgress);
    splide.on('dragged', function () {
      if (bar) bar.style.transition = '';
    });

    splide.mount();
    driveWheel(splide, carousel, state);
    state.splide = splide;

    state.destroy = function () {
      splide.destroy();
      root.__pdpGallery = null;
    };

    root.__pdpGallery = state;
  }

  /* Trackpads and horizontal wheels. Splide's own `wheel` option reads `deltaY`, which
     would take the page's vertical scroll with it, so the gesture is handled here and only
     when it is horizontal. The gallery drags in steps, so this pages the same way rather
     than following the gesture to an intermediate position. */
  function driveWheel(splide, carousel, state) {
    var accumulated = 0;
    var idle = null;

    carousel.addEventListener(
      'wheel',
      function (e) {
        // Vertical intent, a torn-down gallery, or nothing to page through: leave it alone.
        // The listener outlives a destroy, so a re-set-up gallery's own handler owns it now.
        if (Math.abs(e.deltaX) <= Math.abs(e.deltaY)) return;
        if (state.root.__pdpGallery !== state) return;
        if (!carousel.classList.contains('is-overflow')) return;

        e.preventDefault();

        // A line- or page-mode wheel reports 1s and 2s; scale them to something moving.
        accumulated += e.deltaX * (e.deltaMode === 0 ? 1 : 16);
        if (Math.abs(accumulated) >= 40) {
          splide.go(accumulated > 0 ? '>' : '<');
          accumulated = 0;
        }

        // No wheel event says it is the last one, so the end of the gesture is a gap: drop
        // the remainder rather than letting it page on the next flick.
        if (idle) clearTimeout(idle);
        idle = setTimeout(function () {
          idle = null;
          accumulated = 0;
        }, 120);
      },
      { passive: false }
    );
  }

  /* ---------- Zoom lightbox ---------- */

  function stageOf(dialog) {
    return dialog.querySelector('[data-pdp-gallery-lightbox-stage]');
  }

  function resetZoom(stage) {
    if (!stage) return;
    stage.classList.remove('is-zoomed', 'is-panning');
    var img = stage.querySelector('img');
    if (img) img.style.transformOrigin = '';
    stage.setAttribute('aria-label', stage.getAttribute('data-label-zoom-in') || '');
    stage.setAttribute('aria-pressed', 'false');
  }

  // Zoom and pan both move the transform origin, so the point under the pointer is
  // always the point being inspected.
  function originFrom(stage, e) {
    var box = stage.getBoundingClientRect();
    if (!box.width || !box.height) return null;
    var x = Math.min(Math.max((e.clientX - box.left) / box.width, 0), 1) * 100;
    var y = Math.min(Math.max((e.clientY - box.top) / box.height, 0), 1) * 100;
    return x + '% ' + y + '%';
  }

  function toggleZoom(stage, e) {
    if (stage.classList.contains('is-zoomed')) {
      resetZoom(stage);
      return;
    }

    var img = stage.querySelector('img');
    // detail === 0 is keyboard activation: no coordinates, so zoom to the centre.
    if (img && e && e.detail !== 0) {
      var origin = originFrom(stage, e);
      if (origin) img.style.transformOrigin = origin;
    }

    stage.classList.add('is-zoomed');
    stage.setAttribute('aria-label', stage.getAttribute('data-label-zoom-out') || '');
    stage.setAttribute('aria-pressed', 'true');
  }

  function openLightbox(trigger) {
    var root = trigger.closest('[data-pdp-gallery]');
    var dialog = root && root.querySelector('[data-pdp-gallery-lightbox]');
    if (!dialog || typeof dialog.showModal !== 'function') return;

    var img = dialog.querySelector('[data-pdp-gallery-lightbox-img]');
    if (img) {
      clearTimeout(dialog.__clearTimer);
      img.setAttribute('srcset', trigger.getAttribute('data-zoom-srcset') || '');
      img.setAttribute('src', trigger.getAttribute('data-zoom-src') || '');
      img.setAttribute('alt', trigger.getAttribute('data-zoom-alt') || '');
    }

    resetZoom(stageOf(dialog));

    // Every exit path ends in a `close` event, so park the return target there.
    dialog.__returnFocus = trigger;
    dialog.showModal();
  }

  // A swipe ends in a click on the image button, and a pan in one on the zoomed stage
  // — neither is a tap. Keyed by pointerId so a second finger cannot reset the first.
  var pointerOrigins = {};
  var pointerMoved = false;

  document.addEventListener(
    'pointerdown',
    function (e) {
      pointerOrigins[e.pointerId] = { x: e.clientX, y: e.clientY };
      pointerMoved = false;
    },
    true
  );

  document.addEventListener(
    'pointerup',
    function (e) {
      var origin = pointerOrigins[e.pointerId];
      delete pointerOrigins[e.pointerId];
      if (!origin) return;
      pointerMoved =
        Math.abs(e.clientX - origin.x) > 10 || Math.abs(e.clientY - origin.y) > 10;
    },
    true
  );

  // A pan the browser takes over never reaches pointerup, so treat it as a drag.
  document.addEventListener(
    'pointercancel',
    function (e) {
      delete pointerOrigins[e.pointerId];
      pointerMoved = true;
    },
    true
  );

  // Mouse pans on hover, touch only while a finger is down — pointermove covers both.
  document.addEventListener('pointermove', function (e) {
    if (!e.target || !e.target.closest) return;
    var stage = e.target.closest('[data-pdp-gallery-lightbox-stage]');
    if (!stage || !stage.classList.contains('is-zoomed')) return;

    var img = stage.querySelector('img');
    if (!img) return;
    var origin = originFrom(stage, e);
    if (!origin) return;

    stage.classList.add('is-panning');
    img.style.transformOrigin = origin;
  });

  document.addEventListener('click', function (e) {
    if (!e.target || !e.target.closest) return;

    var trigger = e.target.closest('[data-pdp-gallery-zoom]');
    if (trigger) {
      if (pointerMoved && e.detail !== 0) return;
      openLightbox(trigger);
      return;
    }

    var closer = e.target.closest('[data-pdp-gallery-lightbox-close]');
    if (closer) {
      var owner = closer.closest('[data-pdp-gallery-lightbox]');
      if (owner) owner.close();
      return;
    }

    var stage = e.target.closest('[data-pdp-gallery-lightbox-stage]');
    if (stage) {
      if (pointerMoved && e.detail !== 0) return;
      toggleZoom(stage, e);
      return;
    }

    // Clicking the backdrop targets the dialog element itself.
    if (e.target.matches && e.target.matches('[data-pdp-gallery-lightbox]')) {
      e.target.close();
    }
  });

  // `close` does not bubble — listen in the capture phase.
  document.addEventListener(
    'close',
    function (e) {
      var dialog = e.target;
      if (!dialog || !dialog.matches || !dialog.matches('[data-pdp-gallery-lightbox]')) return;

      resetZoom(stageOf(dialog));

      // Cleared after the exit transition so the image does not blank out mid-fade.
      // removeAttribute, not src='' — an empty src re-requests the document.
      var img = dialog.querySelector('[data-pdp-gallery-lightbox-img]');
      if (img) {
        clearTimeout(dialog.__clearTimer);
        dialog.__clearTimer = setTimeout(function () {
          img.removeAttribute('srcset');
          img.removeAttribute('src');
        }, 300);
      }

      var target = dialog.__returnFocus;
      dialog.__returnFocus = null;
      if (target && document.contains(target)) target.focus();
    },
    true
  );

  /* ---------- Boot ---------- */

  function initAll(scope) {
    (scope || document).querySelectorAll('[data-pdp-gallery]').forEach(setup);
  }

  function ready() {
    if (window.Splide) return initAll();
    // Both scripts are deferred in document order, so Splide is normally already here.
    // The poll only covers a slow or blocked vendor request; failing that, the static
    // row in the CSS is the fallback.
    var tries = 0;
    var timer = setInterval(function () {
      if (window.Splide || tries++ > 60) {
        clearInterval(timer);
        if (window.Splide) initAll();
      }
    }, 50);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', ready);
  } else {
    ready();
  }

  /* ---------- Theme editor lifecycle ---------- */

  document.addEventListener('shopify:section:load', function (e) {
    initAll(e.target);
  });

  document.addEventListener('shopify:section:unload', function (e) {
    var galleries = e.target.querySelectorAll('[data-pdp-gallery]');
    for (var i = 0; i < galleries.length; i++) {
      // Drop the return target first — the section is still attached, so the close
      // handler would focus a button that is about to be removed.
      var dialog = galleries[i].querySelector('[data-pdp-gallery-lightbox]');
      if (dialog) {
        dialog.__returnFocus = null;
        clearTimeout(dialog.__clearTimer);
        if (dialog.open) dialog.close();
      }
      if (galleries[i].__pdpGallery) galleries[i].__pdpGallery.destroy();
    }
  });
})();
